/**
 * The glyphs this screen draws that nothing else does.
 *
 * Transcribed from the 17 September 2026 specimen (`1-lista.html`'s `THI` map and `mock.js`'s
 * `ICON`). Four column marks on a 14-box at stroke 1.4 — the roster's own rule, and what makes the
 * two tables' heads read as one system — plus the caret the active head wears and the rectangle the
 * panel toggle draws.
 *
 * Every one of them is `aria-hidden` and none is the only channel: a column head carries its word
 * beside the mark, and the toggle carries its whole name in `aria-label`. What is NOT here is the
 * header's `+`, which is `shared/ui/ScreenHeader`'s, because it is the same mark on three screens.
 */

interface IconProps {
  readonly className?: string;
}

/** A 14-box column mark: the roster's geometry, so the two tables' heads match. */
function ColumnGlyph({ className, children }: IconProps & { readonly children: React.ReactNode }) {
  return (
    <svg
      className={className}
      viewBox="0 0 14 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** ĆWICZENIE — a barbell seen end on. */
export function ExerciseColumnGlyph({ className }: IconProps) {
  return (
    <ColumnGlyph className={className}>
      <path d="M2 5.5v3M4 4v6M4 7h6M10 4v6M12 5.5v3" />
    </ColumnGlyph>
  );
}

/** TYP — four tiles, the shape of a taxonomy. */
export function TypeColumnGlyph({ className }: IconProps) {
  return (
    <ColumnGlyph className={className}>
      <rect x="2" y="2" width="4" height="4" rx="1" />
      <rect x="8" y="2" width="4" height="4" rx="1" />
      <rect x="2" y="8" width="4" height="4" rx="1" />
      <rect x="8" y="8" width="4" height="4" rx="1" />
    </ColumnGlyph>
  );
}

/** PARTIE — a torso. */
export function MusclesColumnGlyph({ className }: IconProps) {
  return (
    <ColumnGlyph className={className}>
      <circle cx="7" cy="2.8" r="1.6" />
      <path d="M4.4 6.2h5.2l-.8 3.4v2.6M5.2 12.2V9.6l-.8-3.4" />
    </ColumnGlyph>
  );
}

/** SPRZĘT — a kettlebell. */
export function EquipmentColumnGlyph({ className }: IconProps) {
  return (
    <ColumnGlyph className={className}>
      <path d="M5.2 5.2V3.6a1.8 1.8 0 0 1 3.6 0v1.6" />
      <path d="M3.2 7.6a3.8 3.8 0 1 1 7.6 0c0 1.9-1 3-1.6 4.4H4.8C4.2 10.6 3.2 9.5 3.2 7.6Z" />
    </ColumnGlyph>
  );
}

/**
 * The caret on the active head. Points DOWN as drawn; the stylesheet rotates it for ascending,
 * which is one transform rather than two drawings that could drift apart.
 */
export function SortCaret({ className }: IconProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
    >
      <path d="m3.5 6 4.5 4.5L12.5 6" />
    </svg>
  );
}

/** The panel toggle: a box with a column down its right-hand side, which is the layout it names. */
export function PanelToggleGlyph({ className }: IconProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
    >
      <rect x="1.5" y="2.5" width="13" height="11" rx="2" />
      <path d="M10 2.5v11" />
    </svg>
  );
}

/* ── The THEATRE's own marks ─────────────────────────────────────────────────────────────────
 *
 * Transcribed from the specimen's `ICON` map at `mock.js` lines 37–60, on the same 16-box at the
 * same weights. They stay in this file for the reason the column marks do: nothing outside
 * `/cwiczenia` draws one, and a shared glyph with a single consumer is a promotion the second-user
 * rule refuses. The three brand marks a material wears are the exception and already live in
 * `./LinkBrandMarks` — a logo is not a house drawing.
 *
 * Every one is `aria-hidden`: each stands inside a control whose whole name is in `aria-label`, or
 * beside a word. */

/** The specimen's stroke construction for this set. */
function TheatreGlyph({
  className,
  strokeWidth = 1.8,
  children,
}: IconProps & { readonly strokeWidth?: number; readonly children: React.ReactNode }) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** Previous. */
export function ChevronLeftGlyph({ className }: IconProps) {
  return (
    <TheatreGlyph className={className}>
      <path d="M10 3.5 5.5 8 10 12.5" />
    </TheatreGlyph>
  );
}

/** Next. */
export function ChevronRightGlyph({ className }: IconProps) {
  return (
    <TheatreGlyph className={className}>
      <path d="m6 3.5 4.5 4.5L6 12.5" />
    </TheatreGlyph>
  );
}

/** Close. */
export function CloseGlyph({ className }: IconProps) {
  return (
    <TheatreGlyph className={className}>
      <path d="M4 4l8 8M12 4l-8 8" />
    </TheatreGlyph>
  );
}

/** Play — the one filled shape in the set, because a play control is a solid triangle everywhere. */
export function PlayGlyph({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="currentColor" aria-hidden focusable="false">
      <path d="M4.5 2.8v10.4L13 8z" />
    </svg>
  );
}

/** Edit. */
export function PencilGlyph({ className }: IconProps) {
  return (
    <TheatreGlyph className={className} strokeWidth={1.7}>
      <path d="m3 13 .8-3.2L11 2.6l2.4 2.4L6.2 12.2z" />
    </TheatreGlyph>
  );
}

/** The ⋯ the ownership menu hangs off. Three dots, filled — a stroked dot is a ring. */
export function MoreGlyph({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="currentColor" aria-hidden focusable="false">
      <circle cx="3.5" cy="8" r="1.4" />
      <circle cx="8" cy="8" r="1.4" />
      <circle cx="12.5" cy="8" r="1.4" />
    </svg>
  );
}

/** Duplicate. */
export function CopyGlyph({ className }: IconProps) {
  return (
    <TheatreGlyph className={className} strokeWidth={1.7}>
      <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" />
      <path d="M10.5 5.5v-2a1 1 0 0 0-1-1h-6a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2" />
    </TheatreGlyph>
  );
}

/** Change the cover. */
export function UploadGlyph({ className }: IconProps) {
  return (
    <TheatreGlyph className={className} strokeWidth={1.7}>
      <path d="M8 11V3.5M4.8 6.7 8 3.5l3.2 3.2M3 13h10" />
    </TheatreGlyph>
  );
}

/** The arrow on a material's row: this one leaves the app. */
export function ExternalGlyph({ className }: IconProps) {
  return (
    <TheatreGlyph className={className} strokeWidth={1.7}>
      <path d="M6.5 3.5h-3v9h9v-3" />
      <path d="M9.5 2.5h4v4M13.5 2.5 7.5 8.5" />
    </TheatreGlyph>
  );
}

/**
 * A trainer's tag — the tag boards' own label shape, at 16 and drawn wherever the trainer's tags are
 * named: the bar over the table, the theatre's block and the manage dialog's title.
 */
export function TagGlyph({ className }: IconProps) {
  return (
    <TheatreGlyph className={className} strokeWidth={1.6}>
      <path d="M2.5 2.5h5l6 6-5 5-6-6z" />
      <circle cx="5.5" cy="5.5" r="1" />
    </TheatreGlyph>
  );
}
